import React from 'react';
import styled from 'styled-components/macro';
import { rgba } from 'polished';
import Isvg from 'react-inlinesvg';
import translate from '../../../components/TranslateHOC';
import drop_down from '../../../img/icons/expand_more.svg';
import ConditionalLink from '../../../components/ConditionalLink';
import { imageFallback, IsvgWrapper } from '../../../helpers';
import ListItemSubtitlesV2 from './ListItemSubtitlesV2';

const ListCardItemHolderMobile = ({
  details,
  isToggleProgramme,
  strings,
  handleProgrammeClick,
  handleKeyboardClick,
}) => {
  return (
    <ListCardItemHolderComponent
      key={details.programme_id}
      show={isToggleProgramme[details.programme_id]}
    >
      <ListCardItemHead
        onClick={() => handleProgrammeClick(details.programme_id)}
        onKeyPress={(e) => handleKeyboardClick(details.programme_id, e)}
        tabIndex="0"
        role="button"
      >
        <ListCardItemTitle>
          <ListCardItemTime>{details.time}</ListCardItemTime> {details.title}
        </ListCardItemTitle>
        <ListCardItemToggle show={isToggleProgramme[details.programme_id]}>
          <IsvgWrapper addClasses="icon">
            <Isvg src={drop_down} />
          </IsvgWrapper>
        </ListCardItemToggle>
      </ListCardItemHead>

      <ListCardItemLink show={isToggleProgramme[details.programme_id]}>
        <ConditionalLink
          to={`/programme/${details.programme_id}`}
          condition={details.available}
        >
          <ListCardItemImageHolder>
            <ListCardItemImage
              src={
                'https://www.s4c.cymru/amg/landscape/45x45/' + details.thumbnail
              }
              alt={details.title}
              onError={(e) =>
                imageFallback(
                  e,
                  'https://www.s4c.cymru/amg/landscape/45x45/clic-c.jpg'
                )
              }
            />
          </ListCardItemImageHolder>
          <ListCardItemCopy>
            <div>{details.short_billing}</div>
            <ListItemSubtitlesV2 details={details} isMobile />
            {!details.available && (
              <ListCardItemTag>{strings.unavailable}</ListCardItemTag>
            )}
          </ListCardItemCopy>
        </ConditionalLink>
      </ListCardItemLink>
    </ListCardItemHolderComponent>
  );
};

export default translate(ListCardItemHolderMobile);

const ListCardItemImageHolder = styled.div`
  border-radius: 6px;
  box-shadow: 0 0 0 1px
    ${(props) =>
      props.theme.name === 'light'
        ? rgba(props.theme.color.blackFull, 0.25)
        : rgba(props.theme.color.whiteFull, 0.25)};
  @include clipping;
  float: left;
  height: 45px;
  margin: 6px 0 0 -59px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease-in;
  width: 45px;
  &:before {
    background: url('../img/icons/image.svg') no-repeat center center;
    background-size: 40px 40px;
    content: '';
    display: block;
    height: 100%;
    left: 0;
    opacity: 0.1;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 5;
  }
`;

const ListCardItemLink = styled.div`
  display: ${(props) => (props.show ? `block` : `none`)};
  ${(props) =>
    props.show &&
    `
      padding: 4px 16px 0 74px;
      max-height: 100%;
      opacity: 1;
      @media screen and (min-width: 768px) {
        padding: 1px 16px 0 77px;
      }
  `}
`;

const ListCardItemToggle = styled.div`
  height: 20px;
  position: absolute;
  right: 12px;
  top: 16px;
  transition: all 0.3s ease-in;
  width: 8px;
  transform: rotate(${(props) => (props.show ? `180deg` : `0deg`)});
  * {
    fill: ${(props) => `${props.theme.color.primaryFull}`};
  }
`;

const ListCardItemHead = styled.div`
  display: flex;
  cursor: pointer;
  padding: 16px 24px 0 16px;
  position: relative;
  &:focus-visible {
    box-shadow: 0px 0px 0px 2px
      ${(props) =>
        props.theme.name === 'light'
          ? `${props.theme.color.statusLightFocus}`
          : `${props.theme.color.statusDarkFocus}`};
    border-radius: 6px;
    outline: none;
  }
  @media screen and (min-width: 768px) {
    padding: 16px 18px 0;
  }
`;

const ListCardItemHolderComponent = styled.div`
  border-radius: 6px;
  margin-bottom: 12px;
  min-height: 35px;
  padding-bottom: 12px;
  position: relative;
  background: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.whiteFull}`
      : '#1d1d1c'};
  box-shadow: ${(props) =>
    props.theme.name === 'light' && `0px 4px 12px rgba(106, 106, 106, 0.25);`};
  z-index: ${(props) => (props.show ? `10` : `5`)};
`;

const ListCardItemTitle = styled.h3`
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.black90}`
      : `${props.theme.color.whiteFull}`};
  font-size: 16px;
  letter-spacing: -0.025em;
  line-height: 1.2;
  margin: 0;
  padding: 0 0 0 58px;
  transition: all 0.1s ease-in;
  @media screen and (min-width: 768px) {
    padding-left: 63px;
  }
`;

const ListCardItemTime = styled.span`
  ${(props) => props.theme.fontBook};
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.black75}`
      : `${props.theme.color.whiteFull}`};
  font-variant-numeric: tabular-nums;
  float: left;
  margin-left: -58px;
  @media screen and (min-width: 768px) {
    margin-left: -63px;
  }
`;

const ListCardItemCopy = styled.div`
  display: inline-block;
  position: relative;
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.black75}`
      : `${props.theme.color.whiteFull}`};
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -0.1px;
  min-height: 44px;
  padding: 4px 0 0;
  @media screen and (min-width: 768px) {
    padding-left: 4px;
  }
`;

const ListCardItemTag = styled.span`
  ${(props) => props.theme.fontBold};
  background-color: ${(props) =>
    props.theme.name === 'light'
      ? rgba(props.theme.color.blackFull, 0.7)
      : rgba(props.theme.color.whiteFull, 0.6)};
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.white90}`
      : `${props.theme.color.black90}`};
  display: inline-block;
  font-size: 9px;
  line-height: 18px;
  letter-spacing: 1.1px;
  margin-top: 9px;
  padding: 0px 8px 0px 6px;
  text-transform: uppercase;
  backdrop-filter: blur(12px);
  border-radius: 3px;
  @media screen and (min-width: 768px) {
    margin-bottom: 4px;
  }
`;

const ListCardItemImage = styled.img`
  position: relative;
  z-index: 10;
`;
